import React, { useState, useEffect, useCallback } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Slide,
  Typography,
  Grid,
  IconButton,
  Tooltip,
  DialogContentText,
  Avatar,
  Tab,
  Tabs,
} from "@mui/material";
import { styled } from "@mui/system";
import { DialogActions } from "@mui/material";
import { LoadingButton } from "@mui/lab";
// atoms
import mealsCategoriesFilterAtom from "src/recoil/atoms/mealsCategoriesFilterAtom";
import mealDetailsPopUpAtom from "src/recoil/atoms/mealDetailsPopUpAtom";
// __apis__
import { fruitOptionsFetcher } from "src/__apis__/meals";
// components
import Iconify from "./Iconify";
import UploadMealPopUp from "./UploadMealPopUp";
import saladOptionsAtom from "src/recoil/atoms/saladOptionsAtom";
import useLocales from "src/hooks/useLocales";
import MealVideoPopUp from "./MealVideoPopUp";
import userAtom from "src/recoil/atoms/userAtom";

// -------------------------------------------------------------------------------------------------------

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide timeout={5} ref={ref} {...props} />;
});

// -------------------------------------------------------------------------------------------------------

const StyledChip = styled(Chip)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightBold,
}));

// -------------------------------------------------------------------------------------------------------

const IngredientItem = ({
  id,
  name,
  category,
  arName,
  serving,
  carbs,
  calcPerPiece,
  calcPerServingRange,
  servingStartValue,
  servingEndValue,
  perPieceServing,
  perPieceName,
  img,
  triggerMealItemDescriptionPopUp,
  videoLink,
  preparationDescription,
}) => {
  const { currentLang } = useLocales();
  const [foodItemVideo, triggerFoodItemVideo] = useState(false);

  const mealCategoriesFilter = useRecoilValue(mealsCategoriesFilterAtom);
  const [fetchingFruitOptions, setFetchingFruitOptions] = useState(false);

  const [replacedItem, setReplacedItem] = useState(false);

  const [fruitOptions, setFruitOptions] = useState([]);

  const fetchFruitOptions = useCallback(async () => {
    setFetchingFruitOptions(true);
    fruitOptionsFetcher(id)
      .then((response) => {
        setFetchingFruitOptions(false);
        setFruitOptions(response);
      })
      .catch((error) => {
        console.log("Error fetching fruit options", error);
      });
  }, [id, setFetchingFruitOptions, setFruitOptions]);

  useEffect(() => {
    if (category === "Fruit") {
      fetchFruitOptions();
    }
  }, [mealCategoriesFilter, category]);

  const [selectedFruitIndex, setSelectedFruitIndex] = useState(0);

  const [selectedServingSize, setSelectedServingSize] = useState(serving);

  const replaceFoodItem = () => {
    if (fruitOptions.length > 0) {
      setReplacedItem(true);
      setSelectedFruitIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % fruitOptions.length;
        const nextFruit = fruitOptions[nextIndex];

        // Calculate the new serving size so that the carbs of the new fruit match the target carbs.
        // Assuming nextFruit.carbs is the carbs per 100g of the fruit.
        const newServingSize = (carbs / nextFruit.carbs) * 100;
        setSelectedServingSize(newServingSize);

        // Here you can also dispatch an action or call an API to update the backend.
        // updateBackend({ ...nextFruit, serving: newServingSize });

        return nextIndex;
      });
    }
  };

  const rangeBasedServing = () => {
    let result = 0;
    const avgEggWeight = (servingStartValue + servingEndValue) / 2;
    result = serving / avgEggWeight;

    return Math.round(result);
  };

  useEffect(() => {
    if (fruitOptions.length > 0) {
      const selectedFruit = fruitOptions[selectedFruitIndex];
      const newServingSize = (carbs / selectedFruit.carbs) * 100;
      setSelectedServingSize(newServingSize);
    }
  }, [selectedFruitIndex, fruitOptions, carbs]);

  return (
    <Card
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 1,
        p: 2,
        borderRadius: 2,
        boxShadow: 1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
        {/* Left side */}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Box
            component="img"
            sx={{ borderRadius: "50%", width: 70, mr: 4, height: 70 }}
            src={
              replacedItem
                ? fruitOptions[selectedFruitIndex].food_item_thumbnail
                : img
            }
          />
          {/* Title and serving */}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {replacedItem ? (
              <Typography variant="body1" fontWeight="bold">
                {currentLang.value === "ar"
                  ? fruitOptions[selectedFruitIndex].ar_name
                  : fruitOptions[selectedFruitIndex].en_name}
              </Typography>
            ) : (
              <Typography variant="body1" fontWeight="bold">
                {currentLang.value === "ar" ? arName : name}
              </Typography>
            )}

            {calcPerPiece ? (
              calcPerServingRange ? (
                <StyledChip
                  label={`${rangeBasedServing()} ${perPieceName}${
                    Math.round(serving / perPieceServing) > 1 ? "s" : ""
                  }`}
                  sx={{ width: "fit-content", mt: 1 }}
                  color="secondary"
                />
              ) : (
                <StyledChip
                  label={`${Math.round(
                    serving / perPieceServing
                  )} ${perPieceName}${
                    Math.round(serving / perPieceServing) > 1 ? "s" : ""
                  }`}
                  sx={{ width: "fit-content", mt: 1 }}
                  color="secondary"
                />
              )
            ) : (
              <StyledChip
                label={`${parseInt(
                  replacedItem ? selectedServingSize : serving,
                  10
                )} ${"gm"}`}
                sx={{ width: 80, mt: 1 }}
                color="secondary"
              />
            )}
            <Typography variant="caption" sx={{ mt: 1 }} color="textSecondary">
              {currentLang.value === "ar"
                ? "هام: يتم قياس أوزان المكونات بعد الطهي."
                : "IMPORTANT: Weights are measured after cooking."}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ flex: 1 }} />
        {/* Right side */}
        <Box>
          {category === "Fruit" && (
            <Tooltip title="Replace Item">
              <IconButton onClick={replaceFoodItem}>
                <Iconify icon="mdi:exchange" />
              </IconButton>
            </Tooltip>
          )}
          {videoLink && (
            <Tooltip title="Watch Video">
              <IconButton onClick={() => triggerFoodItemVideo(true)}>
                <Iconify
                  icon="radix-icons:video"
                  sx={{ color: "error.main", width: 30, height: 30 }}
                />
              </IconButton>
              <MealVideoPopUp
                open={foodItemVideo}
                close={() => triggerFoodItemVideo(false)}
                data={{
                  meal_video: videoLink,
                  meal_name: name,
                  meal_arabic_name: arName,
                }}
              />
            </Tooltip>
          )}

          {preparationDescription !== "Meal item preparation description" &&
            preparationDescription && (
              <Tooltip title="How To Prepare it">
                <IconButton
                  onClick={() =>
                    triggerMealItemDescriptionPopUp({
                      open: true,
                      data: preparationDescription,
                    })
                  }
                >
                  <Iconify
                    icon="material-symbols:info-outline"
                    sx={{ width: 30, height: 30 }}
                  />
                </IconButton>
              </Tooltip>
            )}
        </Box>
      </Box>
    </Card>
  );
};

// -------------------------------------------------------------------------------------------------------

const MealItemDescriptionPopUp = ({
  mealItemDescriptionPopUp,
  closeHandler,
}) => {
  const { translate } = useLocales();

  return (
    <Dialog
      fullWidth
      open={mealItemDescriptionPopUp.open}
      onClose={() => closeHandler()}
    >
      <DialogTitle>
        {translate("componentsTranslations.mealsPopUpTranslations.description")}
      </DialogTitle>
      <DialogContent sx={{ mt: 4 }}>
        <DialogContentText>{mealItemDescriptionPopUp.data}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="error" onClick={() => closeHandler()}>
          {translate("componentsTranslations.mealsPopUpTranslations.desCancel")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// -------------------------------------------------------------------------------------------------------

const SaladOptions = () => {
  const [salad, setSalad] = useRecoilState(saladOptionsAtom);

  const { currentLang } = useLocales();

  const setUser = useRecoilValue(userAtom);

  const handleRemoveSaladItem = (itemId) => {
    // Filter out the item to be removed
    const newSaladItems = salad.saladItems.filter((item) => item.id !== itemId);
    const removedItem = salad.saladItems.find((item) => item.id === itemId);

    // Check if the removed item is found
    if (!removedItem) {
      console.error("Removed item not found");
      return;
    }

    // Calculate the total carbs and calories to redistribute
    const totalCarbsToRedistribute =
      removedItem.carbs * (removedItem.serving / 100);
    const totalCaloriesToRedistribute = removedItem.total_calories;

    // Check if we have any items left to redistribute to
    if (newSaladItems.length === 0) {
      console.error("No items left to redistribute macros");
      return;
    }

    // Calculate the sum of carbs and calories for the remaining items
    const remainingCarbs = newSaladItems.reduce(
      (sum, item) => sum + item.carbs * (item.serving / 100),
      0
    );
    const remainingCalories = newSaladItems.reduce(
      (sum, item) => sum + item.total_calories,
      0
    );

    // Redistribute carbs and calories
    const updatedSaladItems = newSaladItems.map((item) => {
      const itemCarbs = item.carbs * (item.serving / 100);
      const itemCalories = item.total_calories;

      // Determine the proportion of this item's macros to the total
      // Avoid division by zero by adding checks
      const carbProportion =
        remainingCarbs > 0 ? itemCarbs / remainingCarbs : 0;
      const calorieProportion =
        remainingCalories > 0 ? itemCalories / remainingCalories : 0;

      // Redistribute the carbs and calories
      const additionalCarbs = totalCarbsToRedistribute * carbProportion;
      const additionalCalories =
        totalCaloriesToRedistribute * calorieProportion;

      // Calculate the new serving size based on redistributed carbs
      // Avoid division by zero by checking if item.carbs is not zero
      const newServing =
        item.carbs > 0 ? ((itemCarbs + additionalCarbs) / item.carbs) * 100 : 0;

      return {
        ...item,
        serving: newServing,
        total_calories: itemCalories + additionalCalories,
      };
    });

    // Update the salad state with the new items

    setSalad((prevSalad) => ({
      ...prevSalad,
      saladItems: updatedSaladItems,
      total_carb_grams: prevSalad.total_carb_grams - totalCarbsToRedistribute,
      total_calories: prevSalad.total_calories - totalCaloriesToRedistribute,
    }));
  };

  return (
    <Box sx={{ my: 2 }}>
      <Grid container spacing={3}>
        {salad.saladItems.map((saladItem, index) => (
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              {/* Left side */}
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Avatar
                  sx={{ width: 75, height: 75 }}
                  src={saladItem?.food_item_thumbnail || "/static/lunch.jpg"}
                  alt={
                    currentLang.value === "ar"
                      ? saladItem.ar_name
                      : saladItem.en_name
                  }
                />
                <Box sx={{ display: "flex", flexDirection: "column", ml: 2 }}>
                  <Typography variant="subtitle1">
                    {currentLang.value === "ar"
                      ? saladItem.ar_name
                      : saladItem.en_name}
                  </Typography>
                  <StyledChip
                    label={`${parseInt(saladItem?.serving, 10)} ${"gm"}`}
                    sx={{ mt: 1, width: 80 }}
                    color="secondary"
                  />
                </Box>
              </Box>
              {/* Right side */}
              <Box>
                <Tooltip title="I dont like this item">
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveSaladItem(saladItem.id)}
                  >
                    <Iconify
                      sx={{ width: 40, height: 40 }}
                      icon="clarity:remove-line"
                    />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// -------------------------------------------------------------------------------------------------------

function MealDetailsPopUp() {
  const [mealDetailsPopUp, setMealDetailsPopUp] =
    useRecoilState(mealDetailsPopUpAtom);

  const [uploadMealPopUp, triggerUploadMealPopUp] = useState(false);

  const user = useRecoilValue(userAtom);

  const popUpIsTriggered = mealDetailsPopUp.isTriggered;
  const data = mealDetailsPopUp.mealData;

  const [showIngredients, setShowIngredients] = useState(true);
  const [showSalad, setShowSalad] = useState(false);

  const salad = useRecoilValue(saladOptionsAtom);

  const [mealItemDescriptionPopUp, triggerMealItemDescriptionPopUp] = useState({
    open: false,
    data: null,
  });

  const onCloseHandler = () => {
    setMealDetailsPopUp({ isTriggered: false, mealData: null });
  };

  //to handel tabs
  const [value, setValue] = React.useState(0);
  const handelTabs = (e, val) => {
    console.warn(val);
    setValue(val);
  };

  const { translate } = useLocales();

  return (
    <Dialog
      TransitionComponent={Transition}
      open={popUpIsTriggered}
      onClose={onCloseHandler}
      fullWidth
    >
      <DialogTitle>{data?.meal_name}</DialogTitle>
      <DialogContent>
        <Box mt={4}>
          <Tabs value={value} onChange={handelTabs}>
            <Tab
              label={translate(
                "componentsTranslations.mealsPopUpTranslations.ingredients"
              )}
              icon={<Iconify icon="map:food" />}
            />
            {salad.saladItems.length > 0 && data?.meal_index !== "snack" && (
              <Tab
                label={translate(
                  "componentsTranslations.mealsPopUpTranslations.salad"
                )}
                icon={<Iconify icon="fluent:bowl-salad-20-filled" />}
              />
            )}
          </Tabs>
          <TabPanal value={value} index={0}>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2 }}
            >
              {data?.food_items?.map(
                (foodItem) =>
                  parseInt(foodItem.serving, 10) !== 0 && (
                    <IngredientItem
                      key={foodItem.id}
                      id={foodItem.id}
                      name={foodItem.name}
                      category={foodItem.category}
                      arName={foodItem.arabic_name}
                      carbs={foodItem?.carbs}
                      serving={parseInt(foodItem.serving, 10)}
                      calcPerServingRange={parseInt(
                        foodItem.calc_per_serving_range,
                        10
                      )}
                      servingStartValue={parseInt(
                        foodItem.serving_start_value,
                        10
                      )}
                      servingEndValue={parseInt(foodItem.serving_end_value, 10)}
                      calcPerPiece={foodItem.calc_per_piece}
                      perPieceServing={foodItem.per_piece_serving}
                      perPieceName={foodItem.per_piece_name}
                      videoLink={foodItem.video_link}
                      preparationDescription={foodItem?.preparation_description}
                      img={foodItem?.food_item_thumbnail}
                      triggerMealItemDescriptionPopUp={() =>
                        triggerMealItemDescriptionPopUp({
                          open: true,
                          data: foodItem?.preparation_description,
                        })
                      }
                    />
                  )
              )}
            </Box>
            <Typography variant="subtitle1" sx={{ mt: 4 }}>
              {translate(
                "componentsTranslations.mealsPopUpTranslations.description"
              )}
            </Typography>
            <Typography>
              {data?.preparation_description || "No Meal Description For Today"}
            </Typography>
          </TabPanal>
          <TabPanal value={value} index={1}>
            <SaladOptions />
          </TabPanal>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onCloseHandler} color="error">
          {translate("componentsTranslations.mealsPopUpTranslations.cancel")}
        </Button>
        <LoadingButton
          onClick={() => triggerUploadMealPopUp(true)}
          variant="contained"
        >
          {translate(
            "componentsTranslations.mealsPopUpTranslations.actionButton"
          )}
        </LoadingButton>
      </DialogActions>
      {/* Upload meal pop up */}
      <UploadMealPopUp
        mealId={data?.id}
        isTriggered={uploadMealPopUp}
        closeHandler={() => triggerUploadMealPopUp(false)}
      />
      {/* Meal Preparation description pop up */}
      <MealItemDescriptionPopUp
        mealItemDescriptionPopUp={mealItemDescriptionPopUp}
        closeHandler={() =>
          triggerMealItemDescriptionPopUp({ open: false, data: null })
        }
      />
    </Dialog>
  );
}

export default MealDetailsPopUp;

// -------------------------------------------------------------------------------------------------------

//tab panal to show tabs
function TabPanal(props) {
  const { children, value, index } = props;

  return <Box>{value === index && <>{children}</>}</Box>;
}
